/*
 * THE NAME CHANGED. THE SAVED WORK DID NOT MOVE.
 *
 * Every key this app writes carried the old product name, and so did the
 * offline database. A rename that only touched the words on the screen would
 * have left a contractor opening the app one morning to an empty job list with
 * a season of quotes still sitting in the browser under a name nothing reads
 * any more. That is the single most expensive thing a rename can do, so it is
 * handled here, once, before anything else runs.
 *
 * THE RULES, AND WHY.
 *
 * Copy, verify, then remove. The new key is written and read back before the
 * old one is dropped, because a half finished move is worse than no move.
 *
 * Never overwrite. If a new key already holds something, the old one is stale
 * by definition and is left alone rather than allowed to clobber current work.
 *
 * The pre-database projects are deliberately NOT moved. Jobs saved before
 * IndexedDB existed still live under lightsplug.project.* and are read by the
 * one legacy importer in lib/db.ts. Renaming them would hide them from the
 * only code that knows how to bring them forward.
 *
 * Best effort throughout. Private mode and disabled storage throw on the first
 * touch; the app has always had to run without remembering anything, and a
 * failed migration must not be the thing that stops it.
 */

/** Old name to new name, for the keys that are a fixed string. */
const MOVES: [string, string][] = [
  ['lightsplug.account.v1', 'lightsos.account.v1'],
  ['lightsplug.company.v1', 'lightsos.company.v1'],
  ['lightsplug.defaults.v1', 'lightsos.defaults.v1'],
  ['lightsplug.advanced', 'lightsos.advanced'],
  // This one never had the s. It is the AI access code, which is off for now.
  ['lightplug.accessCode', 'lightsos.accessCode'],
]

/**
 * Old prefix to new prefix, for keys with an account id on the end.
 *
 * The letterhead is filed per account, so there is one of these per company
 * that has ever signed in on this browser and the exact names cannot be
 * listed ahead of time.
 */
const PREFIXES: [string, string][] = [['lightsplug.company.', 'lightsos.company.']]

function move(from: string, to: string) {
  const value = localStorage.getItem(from)
  if (value === null) return
  // Something is already there under the new name. Newer wins, always.
  if (localStorage.getItem(to) !== null) return
  localStorage.setItem(to, value)
  // Only once it reads back does the old copy stop being the only copy.
  if (localStorage.getItem(to) === value) localStorage.removeItem(from)
}

/**
 * Carry this browser's settings onto the new names. Call once, at startup,
 * before anything reads them.
 */
export function migrateStorageKeys() {
  try {
    for (const [from, to] of MOVES) move(from, to)

    for (const [from, to] of PREFIXES) {
      // Collected first: renaming while walking the index is how a loop skips
      // half of what it was asked to move.
      const found: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && k.startsWith(from)) found.push(k)
      }
      for (const k of found) move(k, to + k.slice(from.length))
    }
  } catch {
    /* private mode, or storage refused. Nothing was lost; nothing moved. */
  }
}

/*
 * Run on import. main.tsx imports this file before the store so that the move
 * has already happened by the time anything reads a key. Exported as well, so
 * a test can run it against a fixture without importing the whole app.
 */
migrateStorageKeys()
